// iter-81y — Unified "Staffs File" page. Replaces the old separate
// Staff Documents + Staff Trainings pages with one professional experience:
// per-staff row with Upload · Records ZIP · Edit · Dismiss · Print (DCY File Checklist).
// iter-81al — Compliance dashboard overview grid + per-employee profile modal +
// AI bulk uploader with auto-checklist sync + retro AI rescan button.
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, isVaultRelockError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Users, Plus, Upload, FolderArchive, Pencil, UserX, Printer, Loader2,
  ShieldCheck, AlertTriangle, CheckCircle2, Clock, Trash2, ChevronRight,
  Undo2, FileText, RotateCcw, Sparkles, XCircle, LayoutGrid,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/dates";
import AIBulkUploader from "./AIBulkUploader";
import StaffFileProfile from "./StaffFileProfile";

const emptyDraft = () => ({
  staff_name: "", position: "", is_administrator: false,
  opin_number: "",
  date_of_hire: "", first_day_of_work: "",
  date_of_recent_background_check: "",
  items: {}, trainings: {},
});

function StatusPill({ status }) {
  const overall = status?.overall || "ok";
  const map = {
    expired:  { label: "Expired",  cls: "bg-red-100 text-red-800 border-red-200",       Icon: AlertTriangle },
    critical: { label: "≤7 days",  cls: "bg-red-50 text-red-700 border-red-200",        Icon: AlertTriangle },
    warning:  { label: "≤15 days", cls: "bg-amber-50 text-amber-800 border-amber-200",  Icon: Clock },
    notice:   { label: "≤30 days", cls: "bg-stone-50 text-stone-700 border-stone-200",  Icon: Clock },
    ok:       { label: "Current",  cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  };
  const { label, cls, Icon } = map[overall];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

export default function StaffFiles() {
  const [records, setRecords] = useState([]);
  const [dashboard, setDashboard] = useState([]);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDismissed, setShowDismissed] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [dismissOpen, setDismissOpen] = useState(false);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [activeStaff, setActiveStaff] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rescanBusy, setRescanBusy] = useState(false);
  const [view, setView] = useState("dashboard"); // "dashboard" | "list"
  // iter-81ah — surface a "Resume draft" pill on the header when a saved draft
  // exists (either from a previous unfinished add or a session that dropped).
  const [hasNewDraft, setHasNewDraft] = useState(false);
  useEffect(() => {
    const check = () => setHasNewDraft(!!localStorage.getItem("cc_staff_draft_new"));
    check();
    window.addEventListener("storage", check);
    return () => window.removeEventListener("storage", check);
  }, [addOpen]);

  const load = async () => {
    setLoading(true);
    try {
      const [rec, tpl, dash] = await Promise.all([
        api.get("/staff/records"),
        api.get("/staff/checklist-template"),
        api.get("/staff/compliance/dashboard"),
      ]);
      setRecords(Array.isArray(rec.data) ? rec.data : []);
      setTemplate(tpl.data);
      setDashboard(Array.isArray(dash.data?.rows) ? dash.data.rows : []);
    } catch (err) {
      // iter-81an: if it's a vault relock, VaultLock will re-render the lock
      // screen automatically — don't spam a redundant toast on top.
      if (!isVaultRelockError(err)) toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const { active, dismissed } = useMemo(() => {
    const a = [], d = [];
    records.forEach(r => (r.dismissed_at ? d : a).push(r));
    return { active: a, dismissed: d };
  }, [records]);
  const visible = showDismissed ? dismissed : active;

  const openEdit = (r) => { setActiveStaff(r); setEditOpen(true); };
  const openUpload = (r) => { setActiveStaff(r); setUploadOpen(true); };
  const openUploadFromDashboard = (rowId) => {
    const rec = records.find(r => r.id === rowId);
    if (rec) { setActiveStaff(rec); setUploadOpen(true); }
  };
  const openProfile = (r) => { setActiveStaff(r); setProfileOpen(true); };
  const openProfileById = (id) => {
    const rec = records.find(r => r.id === id);
    if (rec) { setActiveStaff(rec); setProfileOpen(true); }
  };
  const openDismiss = (r) => { setActiveStaff(r); setDismissOpen(true); };

  const runAiRescan = async () => {
    if (!window.confirm("Run AI on every existing document to backfill missing data on the checklist? This may take a minute for large files.")) return;
    setRescanBusy(true);
    try {
      const res = await api.post("/staff/documents/ai-rescan");
      const d = res.data || {};
      toast.success(`AI processed ${d.processed || 0} docs · updated ${d.classified || 0} · applied to checklist ${d.checklist_synced || 0}`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRescanBusy(false); }
  };

  const downloadZip = async (r) => {
    try {
      const res = await api.get(`/staff/records/${r.id}/records.zip`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(r.staff_name || "staff").replace(/\s+/g, "_")}-records.zip`;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const printChecklist = async (r, reviewerName) => {
    try {
      const params = { t: Date.now() };
      if (reviewerName) params.reviewer_name = reviewerName;
      const res = await api.get(`/staff/records/${r.id}/checklist.pdf`, {
        responseType: "blob", params,
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // iter-81ab — the current-user name (owner) and any admin staff serve as
  // suggested reviewers. The picker also lets the user type any name.
  const currentUserName = (() => {
    try { return JSON.parse(localStorage.getItem("cc_user") || "{}").name || ""; }
    catch { return ""; }
  })();
  const directorSuggestions = useMemo(
    () => records.filter(r => r.is_administrator && !r.dismissed_at).map(r => r.staff_name),
    [records]
  );

  const reactivate = async (r) => {
    if (!window.confirm(`Reactivate ${r.staff_name}?`)) return;
    try {
      await api.put(`/staff/records/${r.id}/reactivate`);
      toast.success("Reactivated");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // iter-81az — permanent delete for wrong-info entries. Two-step confirm
  // (window.confirm + typed name match) so nobody nukes a real employee by
  // accident. If the record was legitimate but the person left, use Dismiss.
  const deleteStaff = async (r) => {
    const name = (r.staff_name || "").trim();
    if (!window.confirm(
      `Permanently DELETE ${name}?\n\n` +
      `This removes their record AND every uploaded document. ` +
      `It cannot be undone. Use Dismiss instead if they simply left the center.`,
    )) return;
    const typed = window.prompt(`Type the employee's full name to confirm delete:\n\n${name}`);
    if ((typed || "").trim().toLowerCase() !== name.toLowerCase()) {
      toast.error("Delete cancelled — name did not match.");
      return;
    }
    try {
      const res = await api.delete(`/staff/records/${r.id}`);
      const dd = res?.data?.documents_deleted ?? 0;
      toast.success(
        `Deleted ${name}${dd ? ` · ${dd} document${dd === 1 ? "" : "s"} removed` : ""}`,
      );
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <section className="max-w-7xl mx-auto p-6 space-y-8" data-testid="staff-files-page">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] font-semibold text-emerald-700 flex items-center gap-2">
            <Users className="w-3.5 h-3.5" /> Employee compliance
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-medium text-stone-900 tracking-tight">
            Staffs File
          </h1>
          <p className="mt-2 text-sm text-stone-600 max-w-2xl leading-relaxed">
            One place per staff member: DCY checklist, uploaded documents, and the file review sheet
            ready for the licensing binder. Upload, print, ZIP, edit or dismiss - one click each.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {hasNewDraft && (
            <Button
              data-testid="staff-files-resume-draft"
              onClick={() => setAddOpen(true)}
              variant="outline"
              className="rounded-xl h-10 border-amber-400 text-amber-800 hover:bg-amber-50"
            >
              <Undo2 className="w-4 h-4 mr-1" /> Resume draft
            </Button>
          )}
          <Button
            data-testid="staff-files-rescan"
            onClick={runAiRescan}
            disabled={rescanBusy}
            variant="outline"
            className="rounded-xl h-10 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
          >
            {rescanBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            AI rescan
          </Button>
          <Button
            data-testid="staff-files-add"
            onClick={() => setAddOpen(true)}
            className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-10"
          >
            <Plus className="w-4 h-4 mr-1" /> Add staff
          </Button>
          <Button
            data-testid="staff-files-wipe"
            onClick={() => setWipeOpen(true)}
            variant="outline"
            className="rounded-xl h-10 border-red-300 text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-1" /> Reset everything
          </Button>
        </div>
      </header>

      {/* Compliance Dashboard */}
      {!showDismissed && !loading && dashboard.length > 0 && (
        <ComplianceDashboard
          rows={dashboard}
          onOpen={openProfileById}
          onUpload={openUploadFromDashboard}
        />
      )}

      <div className="flex items-center gap-2">
        <div className="inline-flex rounded-xl border border-stone-200 bg-white p-1">
          <button
            data-testid="staff-files-tab-active"
            onClick={() => setShowDismissed(false)}
            className={`px-4 h-9 rounded-lg text-sm font-semibold transition inline-flex items-center gap-2 ${
              !showDismissed ? "bg-stone-900 text-white" : "text-stone-500 hover:text-stone-900"
            }`}
          >
            Active <span className="text-[11px] opacity-70">{active.length}</span>
          </button>
          <button
            data-testid="staff-files-tab-dismissed"
            onClick={() => setShowDismissed(true)}
            className={`px-4 h-9 rounded-lg text-sm font-semibold transition inline-flex items-center gap-2 ${
              showDismissed ? "bg-stone-900 text-white" : "text-stone-500 hover:text-stone-900"
            }`}
          >
            Dismissed <span className="text-[11px] opacity-70">{dismissed.length}</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-stone-500 text-sm">
          <Loader2 className="w-4 h-4 inline-block mr-2 animate-spin" /> Loading staff files…
        </div>
      ) : visible.length === 0 ? (
        <div className="py-20 text-center text-stone-500 text-sm rounded-2xl border border-dashed border-stone-200 bg-white">
          {showDismissed
            ? "No dismissed staff."
            : <>No staff yet. Click <b>Add staff</b> to create the first file.</>}
        </div>
      ) : (
        <div className="rounded-2xl border border-stone-200 bg-white divide-y divide-stone-100" data-testid="staff-files-list">
          {visible.map((r) => (
            <StaffRow
              key={r.id}
              r={r}
              ownerName={currentUserName}
              directorNames={directorSuggestions}
              onOpen={() => openProfile(r)}
              onEdit={() => openEdit(r)}
              onUpload={() => openUpload(r)}
              onZip={() => downloadZip(r)}
              onPrint={(reviewer) => printChecklist(r, reviewer)}
              onDismiss={() => openDismiss(r)}
              onReactivate={() => reactivate(r)}
              onDelete={() => deleteStaff(r)}
            />
          ))}
        </div>
      )}

      {/* Add / Edit dialog */}
      <StaffEditDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        template={template}
        record={null}
        onSaved={load}
      />
      <StaffEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        template={template}
        record={activeStaff}
        onSaved={load}
      />

      {/* AI Bulk uploader (replaces the old single-file uploader) */}
      <AIBulkUploader
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        template={template}
        defaultStaffName={activeStaff?.staff_name || ""}
        onDone={() => { load(); }}
      />

      {/* Employee profile modal */}
      <StaffFileProfile
        open={profileOpen}
        onOpenChange={(o) => { setProfileOpen(o); if (!o) load(); }}
        recordId={activeStaff?.id}
        onEdit={() => { setProfileOpen(false); setEditOpen(true); }}
        onUpload={() => { setProfileOpen(false); setUploadOpen(true); }}
      />

      {/* Dismiss confirm */}
      <DismissDialog
        open={dismissOpen}
        onOpenChange={setDismissOpen}
        staff={activeStaff}
        busy={busy}
        setBusy={setBusy}
        onDone={() => { setDismissOpen(false); load(); }}
      />

      {/* Wipe everything */}
      <WipeDialog
        open={wipeOpen}
        onOpenChange={setWipeOpen}
        onDone={() => { setWipeOpen(false); load(); }}
      />
    </section>
  );
}

// ---- Compliance Dashboard: overview grid ----
function ComplianceDashboard({ rows, onOpen, onUpload }) {
  const totals = useMemo(() => rows.reduce((acc, r) => {
    if (r.overall === "expired") acc.expired += 1;
    else if (r.overall === "attention") acc.attention += 1;
    else if (r.overall === "expiring_soon") acc.expiring += 1;
    else acc.complete += 1;
    return acc;
  }, { complete: 0, attention: 0, expiring: 0, expired: 0 }), [rows]);

  const overallBadge = (code) => {
    const map = {
      complete:      { label: "Complete",   cls: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
      expiring_soon: { label: "Expiring",   cls: "bg-amber-100 text-amber-900 border-amber-200",       Icon: Clock },
      attention:     { label: "Attention",  cls: "bg-red-50 text-red-700 border-red-200",              Icon: AlertTriangle },
      expired:       { label: "Expired",    cls: "bg-red-100 text-red-800 border-red-200",             Icon: XCircle },
    };
    return map[code] || map.complete;
  };

  return (
    <div className="rounded-2xl border border-stone-200 bg-white" data-testid="compliance-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-stone-100 border-b border-stone-100">
        <StatTile Icon={CheckCircle2} label="Complete" value={totals.complete} color="text-emerald-700" />
        <StatTile Icon={Clock} label="Expiring ≤30" value={totals.expiring} color="text-amber-700" />
        <StatTile Icon={AlertTriangle} label="Needs Attention" value={totals.attention} color="text-red-700" />
        <StatTile Icon={XCircle} label="Expired" value={totals.expired} color="text-red-800" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Employee</th>
              <th className="text-left px-3 py-2 font-semibold">Status</th>
              <th className="text-right px-3 py-2 font-semibold">Missing</th>
              <th className="text-right px-3 py-2 font-semibold">Expiring ≤30</th>
              <th className="text-right px-3 py-2 font-semibold">Expired</th>
              <th className="text-right px-3 py-2 font-semibold">Docs</th>
              <th className="text-left px-3 py-2 font-semibold">Last updated</th>
              <th className="text-right px-4 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100" data-testid="compliance-dashboard-rows">
            {rows.map(r => {
              const b = overallBadge(r.overall);
              const B = b.Icon;
              return (
                <tr key={r.id} className="hover:bg-stone-50/50">
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => onOpen(r.id)}
                      className="text-left"
                      data-testid={`dash-open-${r.id}`}
                    >
                      <div className="font-medium text-stone-900 flex items-center gap-2">
                        {r.staff_name}
                        {r.is_administrator && <span className="text-[9px] uppercase tracking-widest font-semibold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded-full">Admin</span>}
                      </div>
                      <div className="text-[11px] text-stone-500">{r.position || "—"}{r.opin_number && <> · OPIN {r.opin_number}</>}</div>
                    </button>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${b.cls}`}>
                      <B className="w-3 h-3" /> {b.label}
                    </span>
                  </td>
                  <td className={`px-3 py-3 text-right font-semibold ${r.missing_count > 0 ? "text-red-700" : "text-stone-400"}`}>{r.missing_count}</td>
                  <td className={`px-3 py-3 text-right font-semibold ${r.expiring_soon_count > 0 ? "text-amber-700" : "text-stone-400"}`}>{r.expiring_soon_count}</td>
                  <td className={`px-3 py-3 text-right font-semibold ${r.expired_count > 0 ? "text-red-800" : "text-stone-400"}`}>{r.expired_count}</td>
                  <td className="px-3 py-3 text-right text-stone-600">{r.docs_count}</td>
                  <td className="px-3 py-3 text-[11px] text-stone-500">{fmtDate(r.last_updated) || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => onOpen(r.id)} className="h-8" data-testid={`dash-open-btn-${r.id}`}>
                        <ChevronRight className="w-3.5 h-3.5" /> Open
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onUpload(r.id)} className="h-8 text-emerald-700" data-testid={`dash-upload-${r.id}`}>
                        <Sparkles className="w-3.5 h-3.5 mr-1" /> Upload
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatTile({ Icon, label, value, color }) {
  return (
    <div className="px-5 py-3 flex items-center gap-3">
      <Icon className={`w-5 h-5 ${color}`} />
      <div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{label}</div>
        <div className={`text-xl font-semibold ${color}`}>{value}</div>
      </div>
    </div>
  );
}

function StaffRow({ r, ownerName, directorNames, onOpen, onEdit, onUpload, onZip, onPrint, onDismiss, onReactivate, onDelete }) {
  const isDismissed = !!r.dismissed_at;
  const [customReviewer, setCustomReviewer] = useState("");
  const [popOpen, setPopOpen] = useState(false);

  const doPrint = (name) => {
    setPopOpen(false);
    onPrint((name || "").trim() || undefined);
  };

  const PrintPicker = (
    <Popover open={popOpen} onOpenChange={setPopOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid={`staff-print-${r.id}`}
          variant="outline" size="sm"
          className="rounded-xl border-emerald-300 text-emerald-800 hover:bg-emerald-50"
        >
          <Printer className="w-4 h-4 mr-1" /> Print
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3 space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
          Print DCY File Checklist &mdash; who is reviewing?
        </div>
        {ownerName && (
          <button
            type="button"
            data-testid={`staff-print-owner-${r.id}`}
            onClick={() => doPrint(ownerName)}
            className="w-full text-left px-3 py-2 rounded-lg border border-stone-200 hover:border-emerald-400 hover:bg-emerald-50 text-sm"
          >
            <div className="text-[10px] uppercase text-emerald-700 font-semibold">Owner</div>
            <div className="font-medium text-stone-900">{ownerName}</div>
          </button>
        )}
        {directorNames.filter(n => n && n !== ownerName).map(n => (
          <button
            key={n}
            type="button"
            data-testid={`staff-print-director-${r.id}-${n.replace(/\s+/g, "_")}`}
            onClick={() => doPrint(n)}
            className="w-full text-left px-3 py-2 rounded-lg border border-stone-200 hover:border-emerald-400 hover:bg-emerald-50 text-sm"
          >
            <div className="text-[10px] uppercase text-emerald-700 font-semibold">Director / Admin</div>
            <div className="font-medium text-stone-900">{n}</div>
          </button>
        ))}
        <div className="pt-1">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1">
            Or type a custom name
          </div>
          <div className="flex gap-2">
            <Input
              data-testid={`staff-print-custom-${r.id}`}
              value={customReviewer}
              onChange={(e) => setCustomReviewer(e.target.value)}
              placeholder="e.g. Jane Doe, Director"
              className="h-9 text-sm"
              onKeyDown={(e) => { if (e.key === "Enter") doPrint(customReviewer); }}
            />
            <Button
              size="sm"
              data-testid={`staff-print-custom-submit-${r.id}`}
              onClick={() => doPrint(customReviewer)}
              disabled={!customReviewer.trim()}
              className="rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white h-9"
            >Print</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );

  return (
    <div className="p-5 flex flex-wrap items-center gap-4" data-testid={`staff-row-${r.id}`}>
      <div className="w-11 h-11 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center font-semibold shrink-0 text-sm">
        {(r.staff_name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
      </div>
      <button
        type="button"
        onClick={onOpen}
        className="flex-1 min-w-[220px] text-left hover:bg-stone-50/50 rounded-lg p-1 -m-1 transition"
        data-testid={`staff-open-${r.id}`}
      >
        <div className="flex items-center gap-2">
          <div className="font-medium text-stone-900">{r.staff_name || "—"}</div>
          {r.is_administrator && (
            <span className="text-[10px] uppercase tracking-widest font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full">
              Admin
            </span>
          )}
          <StatusPill status={r.status} />
        </div>
        <div className="text-xs text-stone-500 mt-0.5">
          {r.position || "—"} · Hired {fmtDate(r.date_of_hire) || "—"}
          {r.opin_number && <>{" · "}<span className="font-semibold text-stone-700">OPIN {r.opin_number}</span></>}
          {isDismissed && <>{" · "}<span className="text-red-600 font-semibold">Dismissed {fmtDate(r.dismissed_at)}</span></>}
        </div>
      </button>
      <div className="flex flex-wrap gap-2 justify-end">
        {!isDismissed && (
          <>
            <Button data-testid={`staff-upload-${r.id}`}   variant="outline" size="sm" onClick={onUpload}   className="rounded-xl"><Upload className="w-4 h-4 mr-1" /> Upload</Button>
            <Button data-testid={`staff-zip-${r.id}`}      variant="outline" size="sm" onClick={onZip}      className="rounded-xl"><FolderArchive className="w-4 h-4 mr-1" /> Records ZIP</Button>
            <Button data-testid={`staff-edit-${r.id}`}     variant="outline" size="sm" onClick={onEdit}     className="rounded-xl"><Pencil className="w-4 h-4 mr-1" /> Edit</Button>
            {PrintPicker}
            <Button data-testid={`staff-dismiss-${r.id}`}  variant="outline" size="sm" onClick={onDismiss}  className="rounded-xl border-red-200 text-red-700 hover:bg-red-50"><UserX className="w-4 h-4 mr-1" /> Dismiss</Button>
            <Button data-testid={`staff-delete-${r.id}`}   variant="outline" size="sm" onClick={onDelete}   className="rounded-xl border-red-300 text-red-800 hover:bg-red-100"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
          </>
        )}
        {isDismissed && (
          <>
            <Button data-testid={`staff-zip-${r.id}`}       variant="outline" size="sm" onClick={onZip}        className="rounded-xl"><FolderArchive className="w-4 h-4 mr-1" /> Records ZIP</Button>
            {PrintPicker}
            <Button data-testid={`staff-reactivate-${r.id}`} variant="outline" size="sm" onClick={onReactivate} className="rounded-xl border-emerald-300 text-emerald-800 hover:bg-emerald-50"><Undo2 className="w-4 h-4 mr-1" /> Reactivate</Button>
            <Button data-testid={`staff-delete-${r.id}`}    variant="outline" size="sm" onClick={onDelete}     className="rounded-xl border-red-300 text-red-800 hover:bg-red-100"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
          </>
        )}
      </div>
    </div>
  );
}

function StaffEditDialog({ open, onOpenChange, template, record, onSaved }) {
  const [draft, setDraft] = useState(emptyDraft());
  const [busy, setBusy] = useState(false);
  const [restored, setRestored] = useState(false);
  const editing = !!record;
  // iter-81ah — Auto-save every draft to localStorage keyed by record id
  // (or "new" for a fresh add). If the vault session dies mid-edit, the
  // form re-opens with a "Resume draft" banner instead of blank.
  const draftKey = `cc_staff_draft_${editing ? record.id : "new"}`;

  useEffect(() => {
    if (open) {
      const cached = localStorage.getItem(draftKey);
      if (cached) {
        try {
          setDraft({ ...emptyDraft(), ...(record || {}), ...JSON.parse(cached) });
          setRestored(true);
          return;
        } catch { /* fall through */ }
      }
      setDraft(record
        ? { ...emptyDraft(), ...record, items: record.items || {}, trainings: record.trainings || {} }
        : emptyDraft());
      setRestored(false);
    }
  }, [open, record, draftKey]);

  // Persist every change (debounced by React's batching). Skip writes when
  // the draft is effectively empty so clicking "Discard draft" actually
  // clears localStorage instead of re-writing a blank object.
  useEffect(() => {
    if (!open) return;
    const isEmpty = !draft.staff_name?.trim() && !draft.position?.trim()
                    && !draft.opin_number?.trim() && !draft.date_of_hire
                    && Object.keys(draft.items || {}).length === 0;
    try {
      if (isEmpty) {
        localStorage.removeItem(draftKey);
      } else {
        localStorage.setItem(draftKey, JSON.stringify(draft));
      }
    } catch { /* ignored */ }
  }, [draft, open, draftKey]);

  const clearDraft = () => {
    try { localStorage.removeItem(draftKey); } catch { /* ignored */ }
    setRestored(false);
  };

  const setItem = (key, patch) => setDraft(d => ({
    ...d,
    items: { ...(d.items || {}), [key]: { ...((d.items || {})[key] || {}), ...patch } },
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.staff_name.trim() || !draft.position.trim()) {
      toast.error("Name and position are required.");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/staff/records/${record.id}`, draft);
      } else {
        await api.post("/staff/records", draft);
      }
      clearDraft();
      toast.success(editing ? "Staff updated" : "Staff added");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const groupedItems = useMemo(() => {
    if (!template) return {};
    const g = {};
    (template.items || []).forEach(it => {
      const s = it.section || "Other";
      if (!g[s]) g[s] = [];
      g[s].push(it);
    });
    return g;
  }, [template]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit staff file" : "Add staff"}</DialogTitle>
          <DialogDescription>
            Fill the DCY checklist below. Every field prints on the landscape file review sheet.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-6 pt-2" data-testid="staff-edit-form">
          {restored && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-900 px-3 py-2 text-xs flex items-center justify-between gap-3" data-testid="staff-draft-resumed-banner">
              <span>
                <b>Draft restored.</b> Your previous entries were kept — keep editing and save when ready.
              </span>
              <button
                type="button"
                onClick={() => { clearDraft(); setDraft(record
                  ? { ...emptyDraft(), ...record, items: record.items || {}, trainings: record.trainings || {} }
                  : emptyDraft()); }}
                className="text-emerald-700 hover:text-emerald-900 font-semibold underline"
                data-testid="staff-draft-discard"
              >Discard draft</button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">Name</Label>
              <Input data-testid="sf-name" value={draft.staff_name}
                onChange={(e) => setDraft({ ...draft, staff_name: e.target.value })}
                required className="mt-2 h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">Position</Label>
              <Input data-testid="sf-position" value={draft.position}
                onChange={(e) => setDraft({ ...draft, position: e.target.value })}
                required className="mt-2 h-11" placeholder="Lead Teacher" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">Date of hire</Label>
              <Input data-testid="sf-doh" type="date" value={draft.date_of_hire || ""}
                onChange={(e) => setDraft({ ...draft, date_of_hire: e.target.value })}
                className="mt-2 h-11" />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm h-11">
                <Checkbox
                  data-testid="sf-admin"
                  checked={!!draft.is_administrator}
                  onCheckedChange={(v) => setDraft({ ...draft, is_administrator: !!v })}
                />
                <span>This staff member is an administrator</span>
              </label>
            </div>
            {/* iter-81z — OPIN number (Ohio Professional Identification Number,
                issued by the Ohio Professional Registry / OPR). Prints on
                the DCY file checklist next to Position. */}
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-stone-500">OPIN number (Ohio Professional Registry)</Label>
              <Input data-testid="sf-opin" type="text" value={draft.opin_number || ""}
                onChange={(e) => setDraft({ ...draft, opin_number: e.target.value })}
                className="mt-2 h-11" placeholder="e.g., OPIN-12345678" />
              <p className="text-[11px] text-stone-500 mt-1">
                Shows on the printed DCY File Checklist header once entered.
              </p>
            </div>
          </div>

          {template && Object.entries(groupedItems).map(([section, items]) => (
            <div key={section} className="border border-stone-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-widest font-semibold text-stone-500 mb-3">
                {section}
              </div>
              <div className="space-y-3">
                {items.map(it => {
                  const st = draft.items?.[it.key] || {};
                  const adminSkip = it.admins_only && !draft.is_administrator;
                  return (
                    <div key={it.key} className={`rounded-lg border p-3 ${adminSkip ? "bg-stone-50 opacity-60" : "border-stone-200"}`}>
                      <div className="flex flex-wrap items-baseline gap-2 mb-2">
                        <div className="font-medium text-sm text-stone-900">{it.label}</div>
                        <span className="text-[10px] uppercase tracking-widest text-stone-500">{it.cadence_label}</span>
                        {adminSkip && <span className="text-[10px] font-semibold text-purple-700">N/A · admins only</span>}
                      </div>
                      {!adminSkip && (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                          <Input
                            data-testid={`sf-item-${it.key}-completed`}
                            type="date" placeholder="Completed"
                            value={st.completed || ""}
                            onChange={(e) => setItem(it.key, { completed: e.target.value })}
                          />
                          {it.expires !== "none" && (
                            <Input
                              data-testid={`sf-item-${it.key}-expires`}
                              type="date" placeholder="Expires"
                              value={st.expires || ""}
                              onChange={(e) => setItem(it.key, { expires: e.target.value })}
                            />
                          )}
                          <Input
                            data-testid={`sf-item-${it.key}-initials`}
                            type="text" maxLength={8}
                            placeholder="Initials"
                            value={st.initials || ""}
                            onChange={(e) => setItem(it.key, { initials: e.target.value.toUpperCase() })}
                            className="uppercase tracking-wider"
                          />
                          <Input
                            data-testid={`sf-item-${it.key}-notes`}
                            type="text" placeholder="Notes"
                            value={st.notes || ""}
                            onChange={(e) => setItem(it.key, { notes: e.target.value })}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl" data-testid="sf-submit">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {editing ? "Save changes" : "Add staff"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UploadDialog({ open, onOpenChange, template, staff, onUploaded }) {
  const [title, setTitle] = useState("");
  const [checklistKey, setChecklistKey] = useState("");
  const [category, setCategory] = useState("other");
  const [expiresOn, setExpiresOn] = useState("");
  const [completionDate, setCompletionDate] = useState("");
  const [initials, setInitials] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(""); setChecklistKey(""); setCategory("other");
      setExpiresOn(""); setCompletionDate(""); setInitials("");
      setFile(null);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a file"); return; }
    if (!title.trim()) { toast.error("Add a title"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("category", category);
      if (staff?.staff_name) fd.append("staff_name", staff.staff_name);
      if (checklistKey) fd.append("checklist_key", checklistKey);
      if (expiresOn) fd.append("expires_on", expiresOn);
      if (completionDate) fd.append("completion_date", completionDate);
      if (initials.trim()) fd.append("reviewer_initials", initials.trim());
      await api.post("/staff/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded && onUploaded();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload document</DialogTitle>
          <DialogDescription>
            For <b>{staff?.staff_name || "this staff"}</b>. PDF or image, max 8 MB.
            When you pick a checklist item, the completion/expiry/initials
            auto-fill on their checklist too.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 pt-2" data-testid="staff-upload-dialog-form">
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500">Title</Label>
            <Input data-testid="su-title" value={title} onChange={(e) => setTitle(e.target.value)} required className="mt-2 h-11" placeholder="e.g., CPR/First Aid card" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">Attach to checklist item</Label>
              <select
                data-testid="su-checklist"
                value={checklistKey}
                onChange={(e) => setChecklistKey(e.target.value)}
                className="mt-2 h-11 w-full rounded-md border border-stone-200 bg-white px-3 text-sm"
              >
                <option value="">— none —</option>
                {(template?.items || []).map(it => (
                  <option key={it.key} value={it.key}>{it.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">Category</Label>
              <select
                data-testid="su-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="mt-2 h-11 w-full rounded-md border border-stone-200 bg-white px-3 text-sm"
              >
                <option value="medical">Medical / TB</option>
                <option value="background_check">Background check</option>
                <option value="training">Training / cert</option>
                <option value="orientation">Orientation</option>
                <option value="education">Education</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">Completion</Label>
              <Input data-testid="su-completion" type="date" value={completionDate} onChange={(e) => setCompletionDate(e.target.value)} className="mt-2 h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">Expires</Label>
              <Input data-testid="su-expires" type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} className="mt-2 h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">Initials</Label>
              <Input data-testid="su-initials" type="text" maxLength={8} value={initials} onChange={(e) => setInitials(e.target.value.toUpperCase())} className="mt-2 h-11 uppercase tracking-wider" placeholder="AB" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500">File</Label>
            <Input data-testid="su-file" type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-2 h-11" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl" data-testid="su-submit">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Upload
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DismissDialog({ open, onOpenChange, staff, busy, setBusy, onDone }) {
  const [reason, setReason] = useState("");
  const [lastDay, setLastDay] = useState("");
  useEffect(() => { if (open) { setReason(""); setLastDay(""); } }, [open]);

  const submit = async () => {
    if (!staff) return;
    setBusy(true);
    try {
      await api.put(`/staff/records/${staff.id}/dismiss`, { reason, last_day: lastDay || null });
      toast.success("Dismissed");
      onDone && onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Dismiss {staff?.staff_name || ""}?</DialogTitle>
          <DialogDescription>
            Their record and all uploaded documents are kept for audit retention.
            You can reactivate anytime.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500">Last day</Label>
            <Input data-testid="dis-last-day" type="date" value={lastDay} onChange={(e) => setLastDay(e.target.value)} className="mt-2 h-11" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500">Reason (optional)</Label>
            <Textarea data-testid="dis-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-2" placeholder="Resigned, terminated for cause, moved, etc." />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">Cancel</Button>
          <Button type="button" disabled={busy} onClick={submit} className="bg-red-600 hover:bg-red-700 text-white rounded-xl" data-testid="dis-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            <UserX className="w-4 h-4 mr-1" /> Dismiss staff
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WipeDialog({ open, onOpenChange, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setConfirm(""); }, [open]);

  const submit = async () => {
    if (confirm !== "WIPE") return;
    setBusy(true);
    try {
      const res = await api.post("/staff/wipe-all", { confirm: "WIPE" });
      toast.success(`Wiped ${res.data.records_deleted} records + ${res.data.documents_deleted} documents.`);
      onDone && onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Reset every staff file?
          </DialogTitle>
          <DialogDescription>
            This deletes <b>every staff record and every uploaded document</b> for
            this center. Cannot be undone. Type <b>WIPE</b> below to confirm.
          </DialogDescription>
        </DialogHeader>
        <div className="pt-2">
          <Input
            data-testid="wipe-confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value.toUpperCase())}
            placeholder="Type WIPE to confirm"
            className="h-11 uppercase tracking-widest"
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">Cancel</Button>
          <Button
            type="button"
            disabled={busy || confirm !== "WIPE"}
            onClick={submit}
            className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
            data-testid="wipe-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
            Delete everything
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
